// Central tunables for the prototype. Every number here is a first-guess
// placeholder picked to make the loop playable, not a balanced value —
// expect to retune once the loop is actually played.

export interface WeatherCondition {
  name: string;
  weight: number;
  /** Output multiplier per producer type id (e.g. "solar", "wind"). A type
   * not listed here is unaffected by weather (implicitly 1.0) — e.g. oil
   * doesn't need an entry. Independent per type on purpose: weather doesn't
   * have to hit every renewable the same way (a storm is bad for solar but
   * great for wind, for instance). */
  outputMultiplierByType: Record<string, number>;
}

export interface ProducerType {
  id: string;
  label: string;
  color: string;
  /** A spawned plant's actual capacityMw is sampled uniformly from
   * [capacityMwMin, capacityMwMax] — not every plant of a given type is
   * identically sized. Set both to the same value for a fixed capacity. */
  capacityMwMin: number;
  capacityMwMax: number;
  /** Initial/default target per phase (0-100%) — also the *only* target
   * for "fixed" plants (never player-edited), and the "on" value each
   * phase toggles to for "switch" plants. */
  defaultDialByPhase: number[];
  /** Max change (percentage points) the plant's live output may ramp at
   * each phase transition (Morning→Midday, Midday→Evening, ... — every
   * transition, uniformly, not just once a day) — models plant inertia;
   * renewables ramp freely, thermal plants don't. Irrelevant for "fixed"
   * plants, which have no inertia concept at all (see controlMode). */
  maxDialRampPerPhase: number;
  /** How the player can influence this plant's output, if at all:
   * - "fixed": not player-controlled at all — no dial, no switch. Output
   *   always instantly equals defaultDialByPhase for the current phase
   *   (adjusted by weather), no ramp/inertia. Models something like
   *   individual rooftop solar: nobody's piloting it, it just does
   *   whatever the sun/weather gives moment to moment.
   * - "switch": a per-phase on/off toggle, no fine-grained percentage —
   *   "on" targets defaultDialByPhase for that phase, "off" targets 0.
   *   Models e.g. wind farms or big solar farms: curtail or don't, no
   *   in-between dial.
   * - "dial": the full 0-100% per-phase target, freely editable. Models
   *   thermal plants that can be throttled to any output level. */
  controlMode: "fixed" | "switch" | "dial";
}

export interface ConsumerType {
  id: string;
  label: string;
  color: string;
  /** Same sampling as ProducerType.capacityMwMin/Max — a spawned
   * consumer's actual capacityMw is uniform in this range. */
  capacityMwMin: number;
  capacityMwMax: number;
  /** Fixed/uncontrollable demand fraction of capacity, by phase. */
  demandPattern: number[];
}

export const PRODUCER_TYPES: Record<string, ProducerType> = {
  solar: {
    id: "solar",
    label: "Solar Plant",
    color: "#facc15",
    capacityMwMin: 30,
    capacityMwMax: 50,
    defaultDialByPhase: [50, 100, 30, 0],
    maxDialRampPerPhase: 100,
    controlMode: "switch",
  },
  solarPrivate: {
    id: "solarPrivate",
    label: "Solar Private",
    color: "#facc45",
    capacityMwMin: 15,
    capacityMwMax: 25,
    defaultDialByPhase: [50, 100, 30, 0],
    maxDialRampPerPhase: 100,
    controlMode: "fixed",
  },
  wind: {
    id: "wind",
    label: "Wind Farm",
    color: "#5eead4",
    capacityMwMin: 20,
    capacityMwMax: 40,
    defaultDialByPhase: [60, 20, 60, 40],
    maxDialRampPerPhase: 100,
    controlMode: "switch",
  },
  oil: {
    id: "oil",
    label: "Oil Plant",
    color: "#94a3b8",
    capacityMwMin: 30,
    capacityMwMax: 50,
    defaultDialByPhase: [80, 80, 80, 80],
    maxDialRampPerPhase: 40,
    controlMode: "dial",
  },
  nuclear: {
    id: "nuclear",
    label: "Nuclear Plant",
    color: "#15a378",
    capacityMwMin: 40,
    capacityMwMax: 60,
    defaultDialByPhase: [80, 80, 80, 80],
    maxDialRampPerPhase: 10,
    controlMode: "dial",
  },
};
export const PRODUCER_TYPE_IDS = Object.keys(PRODUCER_TYPES);

export const CONSUMER_TYPES: Record<string, ConsumerType> = {
  housing: {
    id: "housing",
    label: "Housing",
    color: "#60a5fa",
    capacityMwMin: 15,
    capacityMwMax: 25,
    demandPattern: [0.8, 0.4, 1.0, 0.2],
  },
  factory: {
    id: "factory",
    label: "Factory",
    color: "#f472b6",
    capacityMwMin: 20,
    capacityMwMax: 40,
    demandPattern: [0.4, 1.0, 0.6, 0.3],
  },
  hospital: {
    id: "hospital",
    label: "Hospital",
    color: "#c084fc",
    capacityMwMin: 10,
    capacityMwMax: 20,
    demandPattern: [1.0, 0.9, 0.9, 0.8], // steady, critical, always mostly-on
  },
};
export const CONSUMER_TYPE_IDS = Object.keys(CONSUMER_TYPES);

export interface StorageTypeConfig {
  label: string;
  color: string;
  rateMw: number;
  capacityMwh: number;
}

export const STORAGE_TYPES: Record<"dam" | "battery", StorageTypeConfig> = {
  dam: { label: "Dam", color: "#38bdf8", rateMw: 20, capacityMwh: 80 },
  battery: { label: "Battery", color: "#a3e635", rateMw: 10, capacityMwh: 30 },
};

export const CONFIG = {
  // map
  mapRadius: 6,
  hexSizePx: 40,

  // time
  phasesPerDay: 4,
  phaseNames: ["Morning", "Midday", "Evening", "Night"] as const,

  // line resource (km length budget — see doc/game_design.md)
  startingLineBudgetKm: 40,
  lineBudgetPerDayKm: 8,
  lineCostPerHexEdgeKm: 2,
  dismantleTurns: 2,

  // solver: line resistance/capacity
  lineResistancePerKm: 1,
  lineCapacityMw: 30,

  // control nodes — scarcer resource than lines, like Mini Motorways roundabouts
  startingControlNodeBudget: 2,
  controlNodeBudgetPerDays: 3, // +1 every N days
  controlNodeCost: 1,

  // batteries — player-built storage; dams spawn randomly and are free
  startingBatteryBudget: 1,
  batteryBudgetPerDays: 4, // +1 every N days
  batteryCost: 1,
  damSpawnChancePerDay: 0.25,

  // anger — see doc/game_design.md for the two-meter rationale
  imbalanceAngerGainPerMw: 0.6, // driven by curtailed/wasted supply
  unsatisfiedAngerGainPerMw: 1.0, // driven by unmet demand
  angerDecayPerTurn: 3,
  angerGameOverThreshold: 100,

  // weather — rolled one day ahead so the player gets a forecast. Solar and
  // wind move independently (even in opposite directions) instead of both
  // scaling off one shared "how bad is it" number: clear/calm days favor
  // solar over wind, and storms crush solar but push wind output *above*
  // nameplate (>1.0) — real gusty-weather behavior, and it doubles as a
  // gameplay lever: a storm forecast a day ahead is a warning to dial wind
  // down or have storage ready, or eat oversupply/imbalance anger. Oil
  // isn't listed for any condition, so it's always unaffected (1.0).
  weatherConditions: [
    { name: "Clear", weight: 5, outputMultiplierByType: { solar: 1.0, wind: 0.5 } },
    { name: "Cloudy", weight: 3, outputMultiplierByType: { solar: 0.5, wind: 0.9 } },
    { name: "Storm", weight: 1, outputMultiplierByType: { solar: 0.1, wind: 1.4 } },
  ] satisfies WeatherCondition[],

  // score
  scoreMultiplierPerStreakDay: 0.1,
  scoreMultiplierCap: 3.0,
};
